import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { withRouter } from "react-router-dom";
import styles from "./style.module.scss";
import ApplicationStepper from "./components/application-stepper";
import BusinessInformation from "./components/business-information";
import PersonalInformation from "./components/personal-information";
import BusinessProfile from "./components/business-profile";
import CreditCardProcessing from "./components/credit-card-processing";
import ReviewSubmit from "./components/review-submit";
import FileUpload from "./components/file-upload";
import SignInPersonModal from "./components/sign-in-person-modal";
import SendToMerchantModal from "./components/send-to-merchant-modal";
import useSalesFormStore from "../../../../stores/sales-form";
import {
  businessInformationSchema,
  personalInformationSchema,
  businessProfileSchema,
  creditCardProcessingSchema,
  formSchema
} from "../../validation-schema";
import useUserStore from "../../../../stores/user";
import { getApplicationData, getMccLowRisk, salesAppPost, getMinimumData, getCountries } from "../../../../../api";
import Spinner from '../../../../components/Loaders/Spinner'
import SendOtp from "../merchant-verification/send-otp";
import VerifyOtp from "../merchant-verification/verify-otp";
import { toast } from "react-hot-toast";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import ThankYou from "./components/thank-you";

/**
 * Helper function to transform date from MM/DD/YYYY to YYYY-MM-DD format
 * @param {string} dateString - Date string in MM/DD/YYYY format
 * @returns {string} Date string in YYYY-MM-DD format
 */
const transformDateFormat = (dateString) => {
  if (!dateString) return dateString;
  const [month, day, year] = dateString.split('/');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const formatDate = (dateString) => {
  if (!dateString) return dateString;
  const [datePart] = dateString.split(' ');
  const [year, month, day] = datePart.split('-');
  return `${month}/${day}/${year}`;
};

// Section to step mapping
const SECTION_STEPS = {
  "business-information": 1,
  "personal-information": 2,
  "business-profile": 3,
  "credit-card-processing": 4,
  "review-submit": 7
};

/**
 * Application Form Component
 * Handles the multi-step application process for business onboarding
 * 
 * @param {Object} props - Component props
 * @param {Object} props.history - React Router history object
 * @param {Object} props.location - React Router location object
 * @param {boolean} [props.isMerchantFlow=false] - Flag to determine if the form is in merchant flow
 * @param {Function} [props.onHideBreadcrumbs] - Callback to hide breadcrumbs in parent component
 */
const ApplicationForm = ({ history, location, isMerchantFlow = false, match, onHideBreadcrumbs, ...props }) => {
  const [activeSection, setActiveSection] = useState("business-information");
  const [completedSections, setCompletedSections] = useState(["pre-application"]);
  const [isSendToMerchantModalOpen, setIsSendToMerchantModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pricingModel, setPricingModel] = useState(null);
  const [pricingData, setPricingData] = useState([]);
  const [equipmentData, setEquipmentData] = useState([]);
  const [activeAppData, setActiveAppData] = useState(null);
  const [step, setStep] = useState(0);

  const [formData, setFormData] = useState({
    // Business Information
    type_of_ownership: "",
    legal_business_name: "",
    dba_name: "",
    tax_ein: "",
    premise_type: "",
    ownership_years: 0,
    ownership_months: 0,
    business_type: "",
    tax_exempt: "no",
    business_phone: "",
    business_email: "",
    business_website: "",
    dba_address: "",
    dba_city: "",
    dba_state: "",
    dba_zip: "",
    location_years: 0,
    location_months: 0,
    same_as_dba: "no",
    legal_address: "",
    legal_city: "",
    legal_state: "",
    legal_zip: "",
    legal_phone: "",

    // Personal Information
    personal_same_as_dba_legal: "no",
    personal_first_name: "",
    personal_last_name: "",
    personal_email: "",
    personal_phone: "",
    field_personalstreet: "",
    field_personalcity: "",
    field_personalstate: "",
    field_personalzip: "",
    field_residenceyears: "",
    field_residencemonths: "",
    field_documenttype: "",
    field_documentnumber: "",
    document_country: "",
    field_driverstate: "",
    field_birthdate: "",
    field_ownershippercentage: "",
    field_socialsecurity: "",
    additional_owners: [],

    // Business Profile
    product_service_description: "",
    prod_advertise_description: ['online'],
    is_thirdparty_involved: "no",
    thirdparty_involved_description: "",
    pci_complaint: "no",
    terminated_as_vmdax: "no",
    prior_data_compromise_investigation: "no",
    identified_visa_risk_program: "no",
    visa_risk_program_desc: "",
    is_multiple_locations: "no",
    multi_merchant: "",
    multi_merchant_of: "",
    additional_location: "",
    additional_location_of: "",

    // Files uploaded
    files: [],

    // Credit Card Processing
    currently_accept_vdmax: "yes",
    ways_accepting_cards: [
      "Website",
      "In-Store",
      "On the Go",
      "Over the Phone"
    ],

    online_sales: "1",
    mail_order: "2",
    on_premise_sale: "3",
    telephone_order: "4",
    off_premise_sale: "5",

    sales_keyed: "0",
    sales_swiped: "0",

    monthly_max_sales: "",
    avg_sale_amount: "",
    high_sale_amount: "",
    bank_routing: "",
    bank_account: "",
    bank_name: "",
    business_funding_required: false,
    cheque_processing: "no",
    fraud_chargeback_tool: "no",

  });

  const { agent, app_slug } = match.params;

  // const editslug = useSalesFormStore((state) => state.editSlug);
  const setApplicationData = useSalesFormStore((state) => state.setApplicationData);
  const setMccOptions = useSalesFormStore((state) => state.setMccOptions);
  const isOtpSent = useSalesFormStore((state) => state.isOtpSent);
  const isAuthenticated = useSalesFormStore((state) => state.isAuthenticated);
  const setIsAuthenticated = useSalesFormStore((state) => state.setIsAuthenticated);
  const otpCode = useSalesFormStore((state) => state.otpCode);
  const user = useUserStore((state) => state.user);
  const setEditSlug = useSalesFormStore((state) => state.setEditSlug);
  const setCountryList = useSalesFormStore((state) => state.setCountryList);
  const setMinimumData = useSalesFormStore((state) => state.setMinimumData);
  const minimumData = useSalesFormStore((state) => state.minimumData);
  const setIsSignInPersonModalOpen = useSalesFormStore((state) => state.setIsSignInPersonModalOpen);
  const isSignInPersonModalOpen = useSalesFormStore((state) => state.isSignInPersonModalOpen);
  const otpVerificationTime = useSalesFormStore((state) => state.otpVerificationTime);
  const signerType = useSalesFormStore((state) => state.signerType);
  const setUploadedFiles = useSalesFormStore((state) => state.setUploadedFiles);

  /**
   * Fetches application data and MCC options on component mount
   */
  useEffect(() => {
    if (app_slug) {
      setEditSlug(app_slug);
      getMinimumData(app_slug).then((res) => {
        setMinimumData(res.data);
      });
    }
  }, [app_slug]);

  const setSignerStep = (minimumData, progressionId) => {
    if (minimumData?.signers?.length > 1 && progressionId <= 7 && user == null) {
      let signer = null;
      if (signerType === 'First') {
        signer = minimumData?.signers[0];
      } else {
        signer = minimumData?.signers[1];
      }
      if (signer?.status === 'signed') {
        setStep(8);
      } else {
        setStep(progressionId);
      }
    } else {
      setStep(progressionId);
    }
  }

  const fetchApplicationData = async () => {
    try {
      const headers = {
        OtpCode: otpCode
      };
      const minimumData = await getMinimumData(app_slug);
      setMinimumData(minimumData.data);
      const res = await getApplicationData(app_slug, { headers });
      setIsAuthenticated(true);
      setApplicationData(res.data);
      setUploadedFiles(res?.file_data ? res?.file_data : []);
      const appData = res.data;
      // setStep(appData.progression_id);
      setSignerStep(minimumData.data, appData.progression_id);

      setActiveAppData(appData);

      const typeOfSales = appData?.powerappbusinessprofile?.type_of_sales;
      const originationOfTransaction = appData?.powerappbusinessprofile?.origination_of_transaction;

      if (appData?.pricing) {
        setPricingData(appData?.pricing);
      }
      if (appData?.powerappequipment) {
        setEquipmentData(appData?.powerappequipment);
      }
      setPricingModel(appData?.qualifiedtype);

      setFormData({
        ...formData,
        type_of_ownership: appData.field_ownership,
        legal_business_name: appData.field_legalname,
        dba_name: appData.field_dba,
        tax_ein: appData.field_ein,
        premise_type: appData.premise_type,
        ownership_years: appData.field_ownershipyears,
        ownership_months: appData.field_ownershipmonths,
        business_type: appData.field_mcc,
        tax_exempt: appData?.powerappbusinessprofile?.tax_exempt == 'yes' ? 'yes' : 'no',
        business_phone: appData?.field_dbaphone,
        business_email: appData?.field_businessemail,
        business_website: appData?.field_website,
        dba_address: appData?.field_dbastreet,
        dba_city: appData?.field_dbacity,
        dba_state: appData?.field_dbastate,
        dba_zip: appData?.field_dbazip,
        location_years: appData?.field_locationyears,
        location_months: appData?.field_locationmonths,
        same_as_dba: appData?.same_as_legal == 'yes' ? 'yes' : 'no',
        legal_address: appData?.field_legalstreet,
        legal_city: appData?.field_legalcity,
        legal_state: appData?.field_legalstate,
        legal_zip: appData?.field_legalzip,
        legal_phone: appData?.field_businessphone,

        // Personal Information
        personal_first_name: appData?.field_firstname || '',
        personal_last_name: appData?.field_lastname || '',
        personal_email: appData?.field_email || '',
        personal_phone: appData?.field_personalphone || '',
        field_personalstreet: appData?.field_personalstreet || '',
        field_personalcity: appData?.field_personalcity || '',
        field_personalstate: appData?.field_personalstate || '',
        field_personalzip: appData?.field_personalzip || '',
        field_residenceyears: appData?.field_residenceyears || '',
        field_residencemonths: appData?.field_residencemonths || '',
        field_documenttype: appData?.field_documenttype || '',
        field_documentnumber: appData?.field_documentnumber || '',
        field_driverstate: appData?.field_driverstate || '',
        document_country: appData?.document_country || '',
        field_birthdate: formatDate(appData?.field_birthdate) || '',
        field_ownershippercentage: appData?.field_ownershippercentage || '',
        field_socialsecurity: appData?.field_socialsecurity || '',
        personal_same_as_dba_legal: appData?.personal_same_as_dba_legal || 'no',

        // Business Profile creditcardprocess
        thirdparty_involved_description: appData?.creditcardprocess?.third_party_identity || '',
        pci_complaint: appData?.creditcardprocess?.pci_complaint || 'no',
        terminated_as_vmdax: appData?.creditcardprocess?.terminated_as_vmdax || 'no',
        identified_visa_risk_program: appData?.creditcardprocess?.identified_visa_risk_program || 'no',
        prior_data_compromise_investigation: appData?.creditcardprocess?.prior_data_compromise_invetigation || 'no',


        product_service_description: appData?.powerappbusinessprofile?.product_service_sold || '',
        prod_advertise_description: appData?.powerappbusinessprofile?.prod_advertise_desc ? JSON.parse(appData?.powerappbusinessprofile?.prod_advertise_desc) : [],
        is_thirdparty_involved: appData?.powerappbusinessprofile?.thirdparty_delivered_radio || 'no',
        visa_risk_program_desc: appData?.powerappbusinessprofile?.thirdparty_sales_per_desc || '',
        is_multiple_locations: appData?.powerappbusinessprofile?.is_multiple_locations || 'no',
        multi_merchant: appData?.powerappbusinessprofile?.multi_merchant || '',
        multi_merchant_of: appData?.powerappbusinessprofile?.multi_merchant_of || '',
        additional_location: appData?.powerappbusinessprofile?.additional_location || '',
        additional_location_of: appData?.powerappbusinessprofile?.additional_location_of || '',

        // Credit Card Processing
        currently_accept_vdmax: appData?.creditcardprocess?.currently_processing || 'no',
        ways_accepting_cards: appData?.creditcardprocess?.takingcards ? appData?.creditcardprocess?.takingcards : [],

        internet: typeOfSales?.internet || '',
        on_premise_sale: typeOfSales?.on_premise_sale || '',
        off_premise_sale: typeOfSales?.off_premise_sale || '',
        telephone_order: typeOfSales?.telephone_order || '',
        mail_order: typeOfSales?.mail_order || '',

        sales_swiped: originationOfTransaction?.sales_swiped || '',
        sales_keyed: originationOfTransaction?.sales_keyed || '',

        monthly_max_sales: appData?.bankinfo?.maxsales || '',
        avg_sale_amount: appData?.bankinfo?.ticketaverage || '',
        high_sale_amount: appData?.bankinfo?.tickethighest || '',

        bank_routing: appData?.bankinfo?.bankrouting || '',
        bank_account: appData?.bankinfo?.bankaccount || '',
        bank_name: appData?.bankinfo?.bankname || '',

        fraud_chargeback_tool: appData?.creditcardprocess?.fraud_prevention || false,
        cheque_processing: appData?.powerappbusinessprofile?.cheque_processing || false,
        business_funding_required: appData?.powerappbusinessprofile?.business_funding_required || false,

        // Signers
        additional_owners: mapSigners(appData?.signers) || [],
      });

    } catch (error) {
      if (error?.status === 401) {
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  }

  const mapSigners = (signers) => {
    return signers.map((signer) => ({
      signer_first_name: signer.first_name,
      signer_last_name: signer.last_name,
      signer_email: signer.email,
      signer_phone: signer.phone,
      signer_address: signer.address_street,
      signer_city: signer.address_city,
      signer_state: signer.address_state,
      signer_zip: signer.address_zip_code,
      signer_country: signer.document_country,
      signer_social_security: signer.social_security,
      signer_drivers_license_number: signer.drivers_license_number,
      signer_drivers_license_state: signer.drivers_license_state,
      signer_date_of_birth: formatDate(signer.date_of_birth),
      signer_document_type: signer.document_type,
      signer_document_country: signer.document_country,
      signer_ownership_percentage: signer.ownership_percentage,
      signer_residence_years: signer.residence_years,
      signer_residence_months: signer.residence_months
    }));
  }

  const fetchMccLowRisk = async () => {
    const res = await getMccLowRisk();
    const mccOptions = res.data.map(item => ({
      value: item.id,
      label: item.description
    }));
    setMccOptions(mccOptions);
  }

  useEffect(() => {
    if (app_slug) {
      fetchApplicationData();
      getCountries().then((res) => {
        setCountryList(res.data);
      });
    }
    fetchMccLowRisk();
  }, [app_slug]);

  useEffect(() => {
    fetchApplicationData();
  }, [isAuthenticated]);

  const sections = [
    {
      id: "pre-application",
      label: "Pre-Application",
      completed: completedSections.includes("pre-application"),
      disabled: false,
      step: -1
    },
    {
      id: "business-information",
      label: "Business Information",
      completed: completedSections.includes("business-information"),
      disabled: false,
      step: 0
    },
    {
      id: "personal-information",
      label: "Personal Information",
      completed: completedSections.includes("personal-information"),
      disabled: false,
      step: 1
    },
    {
      id: "business-profile",
      label: "Business Profile",
      completed: completedSections.includes("business-profile"),
      disabled: false,
      step: 2
    },
    {
      id: "credit-card-processing",
      label: "Credit Card Processing",
      completed: completedSections.includes("credit-card-processing"),
      disabled: false,
      step: 3
    },
    {
      id: "review-submit",
      label: "Review & Submit",
      completed: completedSections.includes("review-submit"),
      disabled: false,
      step: 7
    },
  ];

  /**
   * Handles section change in the application form
   * @param {number} step number - ID of the section to change to
   */
  const handleSectionChange = (step) => {
    setStep(step);
  };

  // Schema mapping for each section
  const sectionSchemas = {
    "0": businessInformationSchema,
    "1": personalInformationSchema,
    "2": businessProfileSchema,
    "3": creditCardProcessingSchema,
    "7": formSchema
  };

  // Get validation schema based on active section
  const getValidationSchema = useMemo(() => {
    return sectionSchemas[step] || Yup.object();
  }, [step]);

  /**
   * Gets section-specific data from form values
   * @param {Object} values - Form values
   * @returns {Object} Section-specific data
   */
  const getSectionData = (values) => {
    const baseData = {
      editslug: app_slug,
      step: step + 1,
      // Business Information
      type_of_ownership: values.type_of_ownership,
      legal_business_name: values.legal_business_name,
      dba_name: values.dba_name,
      tax_ein: values.tax_ein,
      premise_type: values.premise_type,
      ownership_years: values.ownership_years,
      ownership_months: values.ownership_months,
      business_type: values.business_type,
      tax_exempt: values.tax_exempt,
      location_years: values.location_years,
      location_months: values.location_months,
      same_as_legal: values.same_as_dba === 'yes' ? 'yes' : 'no',
      business_phone: values.business_phone,
      business_email: values.business_email,
      business_website: values.business_website,
      // Add explicit mappings for DBA fields to match backend expectations
      dba_address: values.dba_address,
      dba_city: values.dba_city,
      dba_state: values.dba_state,
      dba_zip: values.dba_zip,
      // Legal address fields based on same_as_dba logic
      legal_address: values.same_as_dba === 'yes' ? values.dba_address : values.legal_address,
      legal_city: values.same_as_dba === 'yes' ? values.dba_city : values.legal_city,
      legal_state: values.same_as_dba === 'yes' ? values.dba_state : values.legal_state,
      legal_zip: values.same_as_dba === 'yes' ? values.dba_zip : values.legal_zip,
      legal_phone: values.same_as_dba === 'yes' ? values.business_phone : values.legal_phone,
      // Business information fields

      // Personal information field mappings
      personal_same_as_dba_legal: values.personal_same_as_dba_legal,
      personal_first_name: values.personal_first_name,
      personal_last_name: values.personal_last_name,
      personal_email: values.personal_email,
      personal_phone: values.personal_phone,
      field_personalstreet: values.field_personalstreet,
      field_personalcity: values.field_personalcity,
      field_personalstate: values.field_personalstate,
      field_personalzip: values.field_personalzip,
      field_residenceyears: values.field_residenceyears,
      field_residencemonths: values.field_residencemonths,
      field_documenttype: values.field_documenttype,
      field_documentnumber: values.field_documentnumber,
      document_country: values.document_country,
      field_driverstate: values.field_driverstate,
      field_birthdate: values.field_birthdate,
      field_ownershippercentage: values.field_ownershippercentage,
      field_socialsecurity: values.field_socialsecurity,
      signers: values.additional_owners,

      // Business Profile field mappings
      product_service_description: values.product_service_description,
      prod_advertise_description: values.prod_advertise_description,
      is_thirdparty_involved: values.is_thirdparty_involved,
      thirdparty_involved_description: values.thirdparty_involved_description,
      pci_complaint: values.pci_complaint,
      terminated_as_vmdax: values.terminated_as_vmdax,
      prior_data_compromise_investigation: values.prior_data_compromise_investigation,
      identified_visa_risk_program: values.identified_visa_risk_program,
      visa_risk_program_desc: values.visa_risk_program_desc,
      is_multiple_locations: values.is_multiple_locations,
      multi_merchant: values.multi_merchant,
      multi_merchant_of: values.multi_merchant_of,
      additional_location: values.additional_location,
      additional_location_of: values.additional_location_of,

      // Credit Card Processing field mappings
      currently_accept_vdmax: values.currently_accept_vdmax,
      // Combine card acceptance methods into an array for the backend
      ways_accepting_cards: values.ways_accepting_cards,
      // Sales percentages
      type_of_sales: {
        internet: values.internet,
        mail_order: values.mail_order,
        on_premise_sale: values.on_premise_sale,
        telephone_order: values.telephone_order,
        off_premise_sale: values.off_premise_sale
      },
      origination_of_transaction: {
        sales_swiped: values.sales_swiped,
        sales_keyed: values.sales_keyed,
      },
      // Monthly sales amounts with proper field names
      monthly_max_sales: values.monthly_max_sales,
      avg_sale_amount: values.avg_sale_amount,
      high_sale_amount: values.high_sale_amount,
      // Bank verification options - use the correct data type for each field
      bank_routing: values?.bank_routing?.replace(/-/g, ''),
      bank_account: values.bank_account,
      bank_name: values.bank_name,

      fraud_chargeback_tool: values.fraud_chargeback_tool ? 'yes' : 'no', // Expects string
      fraud_prevention: values.fraud_chargeback_tool ? 'yes' : 'no', // Expects string in CreditCardProcess model
      cheque_processing: values.accepting_e_cheks ? 'yes' : 'no', // Send 'yes'/'no' instead of 1/0
      business_funding_required: values.business_funding ? 1 : 0 // Expects integer in BusinessProfile model
    };


    return baseData;
  };

  /**
   * Handles form submission
   * @param {Object} values - Form values
   * @param {Object} formikHelpers - Formik helper functions
   */
  const handleSubmit = async (values, formikHelpers) => {
    try {
      setIsSubmitting(true);
      
      // Validate the entire form before submitting
      const isValid = await validateForm(formikHelpers);
      if (!isValid) {
        toast.error("Please fill in all required fields before proceeding");
        setIsSubmitting(false);
        return;
      }
      
      // Prepare data for submission
      const sectionData = getSectionData(values);
      const transformedValues = {
        ...sectionData,
      };

      // Submit to API
      const res = await salesAppPost(transformedValues);
      if (res.status) {
        // Handle successful submission
        const data = res.data;
        setStep(data.progression_id);
        handleSaveAndNext();
        toast.success('Data saved successfully');
        fetchApplicationData();
      } else {
        // Handle API error response
        toast.error(res.data.message || 'Error saving data');
      }
    } catch (error) {
      // Handle unexpected errors
      console.log(error);
      toast.error(error.response?.data?.message || 'An error occurred while saving');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles continue action in the form
   */
  const handleContinue = () => {
    const currentIndex = sections.findIndex(section => section.id === activeSection);
    if (currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].id);
    }
  };

  /**
   * Handles save and exit action
   */
  const handleSaveAndExit = () => {
    history.push("/v2/applications");
  };

  /**
   * Handles save and next action
   */
  const handleSaveAndNext = () => {
    const currentIndex = sections.findIndex(section => section.id === activeSection);
    if (!completedSections.includes(activeSection)) {
      setCompletedSections([...completedSections, activeSection]);
    }
    if (currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].id);
    }
  };

  /**
   * Validates the entire form and touches all fields to show errors
   * @param {Object} formikProps - The Formik props containing validation functions
   * @returns {boolean} Whether form is valid
   */
  const validateForm = async (formikProps) => {
    const { validateForm, setTouched, errors = {}, values = {} } = formikProps;
    
    // Run validation on all fields
    await validateForm();
    
    // Get all field names from the current values and errors, with safe defaults
    const fieldNames = [
      ...Object.keys(values || {}),
      ...Object.keys(errors || {})
    ];
    
    // Create an object to mark all fields as touched
    const touchedFields = fieldNames.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    
    // Mark all fields as touched to show all errors
    setTouched(touchedFields);
    
    // Return whether the form is valid
    return Object.keys(errors || {}).length === 0;
  };

  /**
   * Handles sign in person action
   */
  const handleSignInPerson = (formikProps) => {
    validateForm(formikProps).then(isValid => {
      // Verify if all required sections are completed
      const requiredSections = ["business-information", "personal-information", "business-profile", "credit-card-processing"];
      const missingRequiredSections = requiredSections.filter(section => !completedSections.includes(section));
      // console.log('missingRequiredSections', missingRequiredSections);
      // if (missingRequiredSections.length > 0) {
      //   toast.error("Please complete all required sections before initiating sign in person");
      //   return;
      // }
      
      if (!isValid) {
        toast.error("Please fill in all required fields before proceeding");
        return;
      }
      
      // If validation passes, open the modal
      setIsSignInPersonModalOpen(true);
    });
  };


  /**
   * Handles send to merchant action
   */
  const handleSendToMerchant = (formikProps) => {
    validateForm(formikProps).then(isValid => {
      // Verify if all required sections are completed
      const requiredSections = ["business-information", "personal-information", "business-profile", "credit-card-processing"];
      const missingRequiredSections = requiredSections.filter(section => !completedSections.includes(section));
      
      // if (missingRequiredSections.length > 0) {
      //   toast.error("Please complete all required sections before sending to merchant");
      //   return;
      // }
      
      if (!isValid) {
        toast.error("Please fill in all required fields before proceeding");
        return;
      }
      
      // If validation passes, open the modal
      setIsSendToMerchantModalOpen(true);
    });
  };

  /**
   * Handles continue in person action
   */
  const handleContinueInPerson = () => {
    setIsSendToMerchantModalOpen(false);
    // Add any additional logic for continuing in person
  };

  /**
   * Handles view applications action
   */
  const handleViewApplications = () => {
    setIsSendToMerchantModalOpen(false);
    history.push("/v2/applications");
  };

  /**
   * Renders the content for the current section
   * @param {Object} formikProps - Formik props
   * @returns {React.ReactNode} Section content
   */
  const renderSectionContent = (formikProps) => {
    switch (step) {
      case 0:
        return <BusinessInformation formik={formikProps} />;
      case 1:
        return <PersonalInformation formik={formikProps} />;
      case 2:
        return <BusinessProfile formik={formikProps} />;
      case 3:
        return <CreditCardProcessing formik={formikProps} />;
      case 7:
        return <ReviewSubmit
          formik={formikProps}
          onSectionChange={handleSectionChange}
          pricingModel={pricingModel}
          pricingData={pricingData}
          equipmentData={equipmentData}
          activeAppData={activeAppData}
          reRender={fetchApplicationData}
        />;
      default:
        return <BusinessInformation formik={formikProps} />;
    }
  };

  // Effect to hide breadcrumbs when showing ThankYou component
  useEffect(() => {
    if (activeAppData && activeAppData.progression_id === 8) {
      onHideBreadcrumbs && onHideBreadcrumbs(true);
    } else {
      onHideBreadcrumbs && onHideBreadcrumbs(false);
    }
  }, [activeAppData, onHideBreadcrumbs]);

  return (
    <div className={styles.container}>
      {loading && (
        <div className={styles.loadingContainer} >
          <Spinner />
        </div>
      )}

      {!loading && (
        <>
          {/* Anonymous user verification */}
          {(!isAuthenticated) && (
            <div>
              {!isOtpSent && (
                <SendOtp
                  data={minimumData}
                  editSlug={app_slug}
                />
              )}

              {isOtpSent && (
                <VerifyOtp
                  data={minimumData}
                  editSlug={app_slug}
                />
              )}
            </div>
          )}
          {/* end Anonymous user verification  */}

          {step < 8 ? (
            <>
              <div className={styles.content}>
                <Formik
                  enableReinitialize
                  initialValues={formData}
                  validationSchema={getValidationSchema}
                  onSubmit={handleSubmit}
                  validateOnChange={true}
                  validateOnBlur={true}
                >
                  {(formikProps) => (
                    <Form
                      className={styles.form}
                      // onSubmit={(e) => {
                      //   // Explicitly prevent default to ensure our handler runs
                      //   e.preventDefault();
                      //   const formValues = formikProps.values;
                      //   // Manually call our submit handler
                      //   handleSubmit(formValues, formikProps);
                      //   // Return false to prevent Formik's default submit
                      //   return false;
                      // }}
                      onSubmit={formikProps.handleSubmit}
                    >
                      <div className={styles.formSection}>
                        {renderSectionContent(formikProps)}
                        <div className={styles.formActions}>
                          <div className={`${styles.buttonsContainer} ${step === 7 ? styles.reviewSubmit : ''}`}>
                            {user ? (
                              <>
                                {step === 7 ? (
                                  <>
                                    {otpVerificationTime === null && (
                                      <>
                                        <ButtonIcon
                                          onClick={handleSaveAndExit}
                                          type="button"
                                          bgColor="#EDEDF4"
                                          textColor="#050506"
                                        >
                                          Save
                                        </ButtonIcon>
                                        <button
                                          className={`${styles.button} ${styles.signInPersonButton}`}
                                          onClick={() => handleSignInPerson(formikProps)}
                                          type="button"
                                        >
                                          Sign in Person
                                        </button>
                                        <button
                                          className={`${styles.button} ${styles.sendToMerchantButton}`}
                                          onClick={() => handleSendToMerchant(formikProps)}
                                          type="button"
                                        >
                                          Send to Merchant
                                        </button>
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <ButtonIcon
                                      onClick={handleSaveAndExit}
                                      type="button"
                                      bgColor="#FFF"
                                      textColor="#254FE2"
                                    >
                                      Save
                                    </ButtonIcon>
                                    <ButtonIcon
                                      onClick={handleSendToMerchant}
                                      type="button"
                                      bgColor="#EDEDF4"
                                      textColor="#050506"
                                    >
                                      Send to Merchant
                                    </ButtonIcon>

                                    <ButtonIcon
                                      type="submit"
                                      bgColor="#4c70ef"
                                      textColor="#fff"
                                      isLoading={isSubmitting}
                                    >
                                      Save and next
                                    </ButtonIcon>
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <ButtonIcon
                                  type="submit"
                                  bgColor="#4c70ef"
                                  textColor="#fff"
                                  isLoading={isSubmitting}
                                >
                                  Save and next
                                </ButtonIcon>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className={styles.right}>
                        <ApplicationStepper
                          sections={sections}
                          activeSection={activeSection}
                          onSectionChange={handleSectionChange}
                          step={step}
                        />
                        <div className={styles.fileUploadContainer}>
                          <FileUpload formik={formikProps} />
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>

              <SignInPersonModal
                isOpen={isSignInPersonModalOpen}
                onClose={() => setIsSignInPersonModalOpen(false)}
                minimumData={minimumData}
                reRender={fetchApplicationData}
              />

              <SendToMerchantModal
                isOpen={isSendToMerchantModalOpen}
                onClose={() => setIsSendToMerchantModalOpen(false)}
                merchantLink={window.location.href}
                onContinue={handleContinueInPerson}
                onViewApplications={handleViewApplications}
              />
            </>
          ) : (
            <>
              <ThankYou
                appData={activeAppData}
                appSlug={app_slug}
                reRender={fetchApplicationData}
              />
            </>
          )}
        </>
      )}

    </div>
  );
};

export default withRouter(ApplicationForm); 